<template>
    <div class="relative font-sans min-w-[240px]">
        <vx-selectable
            :modelValue="selected.value"
            @update:modelValue="handleRangeOption"
            :options="optionsWithCustom"
            :reduce="(option: RangeOption) => option.value"
            label="label"
            :clearable="false"
            size="lg"
        />
        <transition
            enter-from-class="opacity-0 -translate-y-4"
            enter-to-class="opacity-100 translate-y-0"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 -translate-y-4"
        >
            <div v-if="isDatePickerOpened" class="absolute z-20 mb-8 inset-e-0 transition" @click.stop>
                <vx-date-picker
                    mode="date"
                    :modelValue="datePickerModel"
                    @update:modelValue="handleDatePickerRange"
                    is-range
                    v-on-click-outside="closePicker"
                    :timezone="timezone"
                    :minDate="computedMinDate ?? undefined"
                    :maxDate="computedMaxDate ?? undefined"
                    @dayclick="handleDayClick"
                />
            </div>
        </transition>
    </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, nextTick } from 'vue';
import { VxSelectable, VxDatePicker } from '@voxie/frontend-components';
import { vOnClickOutside } from '@vueuse/components';
import dayjs from '~/utils/dayjs';
import type { Dayjs } from 'dayjs';
import { shortDate } from '~/utils/date';

export interface DateRangeValue {
    start_date: string;
    end_date: string;
}

export interface RangeOption {
    value: string;
    label: string;
    start_date?: string;
    end_date?: string;
}

const props = withDefaults(
    defineProps<{
        modelValue: DateRangeValue;
        options?: RangeOption[];
        formatRange?: (start: Dayjs, end: Dayjs) => DateRangeValue;
        minDate?: Date | null;
        maxDate?: Date | null;
        maxRangeDays?: number | null;
        timezone?: string;
    }>(),
    {
        options: undefined,
        formatRange: undefined,
        minDate: null,
        maxDate: null,
        maxRangeDays: null,
        timezone: () => dayjs.tz.guess(),
    }
);

const emit = defineEmits<{
    (e: 'update:modelValue', value: DateRangeValue): void;
}>();

const DEFAULT_FORMAT = 'YYYY-MM-DD';

const lastNDays = (days: number) => ({
    start_date: dayjs().subtract(days - 1, 'day').format(DEFAULT_FORMAT),
    end_date: dayjs().format(DEFAULT_FORMAT),
});

const defaultPresets: RangeOption[] = [
    { value: 'last_7_days', label: 'Last 7 days', ...lastNDays(7) },
    { value: 'last_14_days', label: 'Last 14 days', ...lastNDays(14) },
    { value: 'last_30_days', label: 'Last 30 days', ...lastNDays(30) },
    { value: 'last_90_days', label: 'Last 90 days', ...lastNDays(90) },
    {
        value: 'month_today',
        label: 'Current month',
        start_date: dayjs().startOf('month').format(DEFAULT_FORMAT),
        end_date: dayjs().format(DEFAULT_FORMAT),
    },
    {
        value: 'previous_month',
        label: 'Last month',
        start_date: dayjs().subtract(1, 'month').startOf('month').format(DEFAULT_FORMAT),
        end_date: dayjs().subtract(1, 'month').endOf('month').format(DEFAULT_FORMAT),
    },
    { value: 'custom', label: 'Custom period' },
];

const presetOptions = computed<RangeOption[]>(() => props.options ?? defaultPresets);

const findPreset = (value: DateRangeValue): RangeOption | null =>
    presetOptions.value.find(
        (o) => o.start_date === value.start_date && o.end_date === value.end_date
    ) || null;

const buildCustomOption = (value: DateRangeValue): RangeOption => ({
    value: 'selected_custom',
    label: `${shortDate(value.start_date)} - ${shortDate(value.end_date)}`,
    start_date: value.start_date,
    end_date: value.end_date,
});

const customOption = ref<RangeOption | null>(null);
const selected = ref<RangeOption>(findPreset(props.modelValue) ?? buildCustomOption(props.modelValue));

if (!findPreset(props.modelValue)) {
    customOption.value = selected.value;
}

const optionsWithCustom = computed<RangeOption[]>(() =>
    customOption.value ? [...presetOptions.value, customOption.value] : presetOptions.value
);

watch(
    () => props.modelValue,
    (val) => {
        const preset = findPreset(val);
        if (preset) {
            selected.value = preset;
            customOption.value = null;
            return;
        }

        // Preserve the existing custom option if it matches — keeps the user-friendly
        // label built from raw picked Dates when the parent echoes our own emit back.
        if (
            customOption.value &&
            customOption.value.start_date === val.start_date &&
            customOption.value.end_date === val.end_date
        ) {
            selected.value = customOption.value;
            return;
        }

        const custom = buildCustomOption(val);
        selected.value = custom;
        customOption.value = custom;
    },
    { deep: true }
);

const clickedDay = ref<Date | null>(null);

const handleDayClick = (event: { date: Date }) => {
    if (!clickedDay.value) {
        clickedDay.value = event.date;
    }
};

const computedMaxDate = computed<Date | null>(() => {
    if (props.maxRangeDays !== null && clickedDay.value) {
        const capped = dayjs(clickedDay.value).add(props.maxRangeDays, 'day').toDate();
        if (props.maxDate && capped > props.maxDate) {
            return props.maxDate;
        }
        return capped;
    }
    return props.maxDate;
});

const computedMinDate = computed<Date | null>(() => {
    if (props.maxRangeDays !== null && clickedDay.value) {
        const capped = dayjs(clickedDay.value).subtract(props.maxRangeDays, 'day').toDate();
        if (props.minDate && capped < props.minDate) {
            return props.minDate;
        }
        return capped;
    }
    return props.minDate;
});

const defaultPickerModel = () => {
    const base = props.maxDate ? dayjs(props.maxDate).tz(props.timezone) : dayjs().tz(props.timezone);
    return {
        start: base.subtract(2, 'day').startOf('day').toDate(),
        end: base.startOf('day').toDate(),
    };
};

const isDatePickerOpened = ref(false);
const datePickerModel = ref(defaultPickerModel());

const handleRangeOption = (optionValue: string) => {
    const option = optionsWithCustom.value.find((o) => o.value === optionValue);
    if (!option) {
        return;
    }

    if (option.value === 'custom' || option.value === 'selected_custom') {
        isDatePickerOpened.value = true;
        return;
    }

    selected.value = option;
    customOption.value = null;
    if (option.start_date !== undefined && option.end_date !== undefined) {
        emit('update:modelValue', { start_date: option.start_date, end_date: option.end_date });
    }
};

const handleDatePickerRange = async ({ start, end }: { start: Date; end: Date }) => {
    const startDayjs = dayjs(start);
    const endDayjs = dayjs(end);
    const range: DateRangeValue = props.formatRange
        ? props.formatRange(startDayjs, endDayjs)
        : {
              start_date: startDayjs.format(DEFAULT_FORMAT),
              end_date: endDayjs.format(DEFAULT_FORMAT),
          };

    const custom: RangeOption = {
        value: 'selected_custom',
        label: `${shortDate(start)} - ${shortDate(end)}`,
        start_date: range.start_date,
        end_date: range.end_date,
    };
    customOption.value = custom;
    selected.value = custom;
    isDatePickerOpened.value = false;
    datePickerModel.value = defaultPickerModel();

    emit('update:modelValue', range);

    await nextTick();
    clickedDay.value = null;
};

const closePicker = () => {
    isDatePickerOpened.value = false;
    clickedDay.value = null;
};
</script>
